import React, { useState } from 'react';
import { DonutChart, Toggle, ToggleItem } from '@tremor/react';

/** Tremor color names for airline slices - cycles for many airlines */
const AIRLINE_COLORS = [
    'blue', 'green', 'amber', 'red', 'violet',
    'fuchsia', 'cyan', 'lime', 'orange', 'indigo',
    'teal', 'yellow', 'purple', 'rose', 'sky',
];

/** Hex colors for legend swatches - must match AIRLINE_COLORS order */
const SWATCH_HEX = [
    '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6',
    '#d946ef', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
    '#14b8a6', '#eab308', '#a855f7', '#f43f5e', '#0ea5e9',
];

const VIEW_OPTIONS = [
    { value: 'total', text: 'Total' },
    { value: 'preAdvised', text: 'Pre-Advised' },
    { value: 'nonAdvised', text: 'Non-Advised' },
];

const MovementAirlinePieChart = ({ airlines, title, value, onValueChange }) => {
    const [internalView, setInternalView] = useState('total');
    const view = value != null ? value : internalView;
    const setView = onValueChange || setInternalView;

    if (!airlines) return null;

    const normalized = Array.isArray(airlines)
        ? airlines.reduce((acc, a) => {
            if (a?.code != null) acc[a.code] = a;
            return acc;
        }, {})
        : airlines;

    const airlineCodes = Object.keys(normalized || {});
    if (airlineCodes.length === 0) return null;

    const getAirlineValue = (airline, viewMode) => {
        if (!airline) return 0;
        if (airline.total != null && (airline.preAdvised != null || airline.outbound == null)) {
            if (viewMode === 'total') return airline.total ?? 0;
            if (viewMode === 'preAdvised') return airline.preAdvised ?? 0;
            if (viewMode === 'nonAdvised') return airline.nonAdvised ?? 0;
            return airline.total ?? 0;
        }
        const ob = airline.outbound || {};
        const ib = airline.inbound || {};
        const total = (ob.total || 0) + (ib.total || 0);
        const preAdvised = (ob.preAdvised || 0) + (ib.preAdvised || 0);
        const nonAdvised = (ob.nonAdvised || 0) + (ib.nonAdvised || 0);
        if (viewMode === 'total') return total;
        if (viewMode === 'preAdvised') return preAdvised;
        if (viewMode === 'nonAdvised') return nonAdvised;
        return total;
    };

    const data = airlineCodes.map((code, i) => ({
        name: normalized[code]?.airline ?? code,
        value: getAirlineValue(normalized[code], view),
        tremorColor: AIRLINE_COLORS[i % AIRLINE_COLORS.length],
        swatchHex: SWATCH_HEX[i % SWATCH_HEX.length],
    })).filter((d) => d.value > 0);

    if (data.length === 0) return null;

    const total = data.reduce((sum, d) => sum + d.value, 0);

    const viewLabel = VIEW_OPTIONS.find((o) => o.value === view)?.text ?? 'Total';

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {title && (
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '8px', width: '100%', textAlign: 'center' }}>
                    {title}
                </div>
            )}
            <div style={{ marginBottom: '12px' }}>
                <Toggle color="zinc" value={view} onValueChange={setView}>
                    {VIEW_OPTIONS.map((opt) => (
                        <ToggleItem key={opt.value} value={opt.value} text={opt.text} />
                    ))}
                </Toggle>
            </div>
            <DonutChart
                data={data.map(({ name, value }) => ({ name, value }))}
                index="name"
                category="value"
                variant="pie"
                valueFormatter={(v) => `${v} (${total ? Math.round((v / total) * 100) : 0}%)`}
                colors={data.map((d) => d.tremorColor)}
                showAnimation={true}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '12px', alignItems: 'center', maxHeight: '200px', overflowY: 'auto' }}>
                {data.map(({ name, value, swatchHex }) => (
                    <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#374151' }}>
                        <span
                            style={{
                                width: '12px',
                                height: '12px',
                                borderRadius: '2px',
                                backgroundColor: swatchHex,
                            }}
                        />
                        <span>{name}: {value} ({total ? Math.round((value / total) * 100) : 0}%)</span>
                    </div>
                ))}
                <div style={{ marginTop: '4px', paddingTop: '8px', borderTop: '1px solid #e5e7eb', fontSize: '13px', fontWeight: 600, color: '#1f2937' }}>
                    {viewLabel} Total: {total}
                </div>
            </div>
        </div>
    );
};

export default MovementAirlinePieChart;
